import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import Button from 'components/Button';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import licenseSelectors, {
   isUnlimited,
   LICENSE_UNLIMITED,
   LICENSE_UNLIMITED_NUM_KEYS,
} from 'redux/license/selectors';
import {Entry} from 'components/Entry';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {layoutSelectors} from 'features/layout/redux';
import {loginSelectors} from 'features/login/redux';
import {formatDateOrNever} from 'common/dateUtils';
import {formatSerialNumber} from 'common/stringUtils';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {some} from 'lodash';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {useActivateLicense} from 'redux/license/hooks/useActivateLicense';
import {enums} from 'types/enums';
import {LoaderInline} from 'components/Loader';
import AsyncContainer from 'components/AsyncContainer';
import {useUploadLicense} from 'redux/license/hooks/useUploadLicense';
import {useModal} from 'common/hooks/useModal';
import {CertDialog} from 'components/dialogs/CertDialog';
import {useIsPermittedByRole} from 'common/hooks/useIsPermittedByRole';
import {AdminResourceType} from 'types/diplomat/settings/adminroles/AdminResourceType';

type FileLicenseDialogBoxProps = {close: () => void};

const useFileLicenseDialogBoxStyles = makeStyles((theme) => ({
   root: {
      padding: theme.spacing(3),
   },
   button: {
      textAlign: 'center',
   },
}));

const FileLicenseDialogBox = ({close}: FileLicenseDialogBoxProps) => {
   const {t} = useTranslation();
   const classes = useFileLicenseDialogBoxStyles();
   const license = useSelector(licenseSelectors.getLicense);
   const {openpgp, ssh, ssl} = useSelector(layoutSelectors.keysCountSelector);
   const transactions = useSelector(layoutSelectors.transactionsSelector);
   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);
   const isAdmin = useSelector(loginSelectors.isAdmin);
   const isPermittedByRole = useIsPermittedByRole();
   const isSettingsPermitted = isPermittedByRole(AdminResourceType.enum.SETTINGS);

   const serialNumber = React.useMemo(() => formatSerialNumber(license.serialNumber), [
      license.serialNumber,
   ]);

   const YES = t('YES');
   const NO = t('NO');

   const isCloudConnectors = React.useMemo(
      () =>
         some(license.features, (fName) =>
            [
               DiplomatFeature.ALL_CLOUDS,
               DiplomatFeature.GOOGLE_CLOUD_TRANSPORT,
               DiplomatFeature.AMAZONS3_TRANSPORT,
               DiplomatFeature.ORACLE_CLOUD_TRANSPORT,
               DiplomatFeature.SHAREFILE_TRANSPORT,
               DiplomatFeature.AZURE_TRANSPORT,
               DiplomatFeature.BOX_TRANSPORT,
               DiplomatFeature.DROPBOX_TRANSPORT,
               DiplomatFeature.BISCOM,
            ].find((f) => f.id === fName)
         ),
      [license.features]
   );

   const {request: checkForNewLicense, state: _isNewLicenseAvailable} = useGetApi<{
      licenseEdition: string;
   }>(`${api.license}?operation=checkForNewLicense`);
   React.useEffect(() => {
      checkForNewLicense();
   }, [checkForNewLicense]);
   const isNewLicenseAvailable = _isNewLicenseAvailable?.licenseEdition;

   const {activateNewLicense, newLicenseActivated, licenseIsLoading} = useActivateLicense();

   const sftpServerValue = license.numSftpServerUsers
      ? license.numSftpServerUsers === LICENSE_UNLIMITED
         ? YES
         : license.numSftpServerUsers + t(' users')
      : NO;

   const {
      open: isCertDialogOpened,
      handleOpen: openCertDialog,
      handleClose: closeCertDialog,
   } = useModal();

   const uploadLicsense = useUploadLicense(closeCertDialog);

   return (
      <AsyncContainer loading={licenseIsLoading}>
         {isSettingsPermitted && (
            <Grid container justifyContent="center">
               <Button onClick={openCertDialog}>{t('Update license')}</Button>
               {isCertDialogOpened && (
                  <CertDialog
                     data={{
                        title: t('License'),
                        info: t('Paste the contents of the updated license file below'),
                     }}
                     onClose={uploadLicsense}
                  />
               )}
            </Grid>
         )}
         <Grid className={classes.root} container spacing={2}>
            {isAdmin && isSettingsPermitted && isNewLicenseAvailable && !newLicenseActivated && (
               <Entry
                  name={t('A new license is available')}
                  value={
                     <PrimaryButton onClick={activateNewLicense} disabled={licenseIsLoading}>
                        {t('Activate')}
                        {licenseIsLoading && <LoaderInline />}
                     </PrimaryButton>
                  }
               />
            )}
            <Entry name={t('License Type')} value={license.type} />
            <Entry name={t('License ID')} value={serialNumber} />
            <Entry name={t('Expires')} value={formatDateOrNever(license.expires)} />
            <Entry name={t('Edition')} value={enums.LicenseEdition[license.edition]} />
            <Entry name={t('SFTP Server')} value={sftpServerValue} />
            <Entry
               name={t('Edge Gateway')}
               value={isFeatureSupported(DiplomatFeature.EDGE_GATEWAY) ? YES : NO}
            />
            {/* <Entry name={t('Creation Date')} value={formatDateTime(+license.createTime)} /> */}
            <Entry
               name={t('Cloud Storage Connectors')}
               value={
                  isCloudConnectors ? YES : NO
                  // license.numClouds === LICENSE_UNLIMITED ? t('UNLIMITED') : license.numClouds
               }
            />
            <Entry
               name={t('API & Invocation')}
               value={isFeatureSupported(DiplomatFeature.THIRD_PARTY_SCHEDULING) ? YES : NO}
            />
            <Entry
               name={t('Remote Agents')}
               value={isFeatureSupported(DiplomatFeature.CLOUD_TRANSPORT) ? YES : NO}
            />
            <Entry
               name={t('Concurrent Admin Logins')}
               value={isUnlimited(license.numClients) ? YES : NO}
            />
            <Entry name={t('Job Monitor')} value={isUnlimited(license.numJobMonitors) ? YES : NO} />
            <Entry
               name={t('Transactions Allowed')}
               value={
                  license.numTransactions === LICENSE_UNLIMITED
                     ? t('UNLIMITED')
                     : license.numTransactions
               }
            />
            <Entry name={t('Transactions Defined')} value={transactions.length} />
            <Entry
               name={t('Key Ring Slots per Type')}
               value={
                  license.numKeys === LICENSE_UNLIMITED_NUM_KEYS ? t('UNLIMITED') : license.numKeys
               }
            />
            <Entry name={t('OpenPGP Keys In Use')} value={openpgp} />
            <Entry name={t('SSH Client Keys In Use')} value={ssh} />
            <Entry name={t('SSL Certificates In Use')} value={ssl} />
            <Grid xs={12} className={classes.button} item>
               <Button onClick={close}>{t('Ok')}</Button>
            </Grid>
         </Grid>
      </AsyncContainer>
   );
};

type Props = {
   onClose: () => void;
};
export const FileLicenseDialog = ({onClose}: Props) => {
   return (
      <Dialog maxWidth="md" header={DialogTypes.FileLicenseDialogBox.dialogName} close={onClose}>
         <FileLicenseDialogBox close={onClose} />
      </Dialog>
   );
};

export default FileLicenseDialog;
